import type { SelectedFilterOption } from 'features/charts-v2/types'

/**
 * A `SelectedFilterOption` as the server sends it. The backend's
 * `SelectedFilterOptionV2` defaults `numericValue`, `min`, `max` and
 * `valueType` to `None`, so every filter in a `FilterStateV2` response carries
 * them as JSON `null`, where the client's own type has them optional (absent).
 */
export type ServerSelectedFilterOption = {
  [K in keyof SelectedFilterOption]: SelectedFilterOption[K] | null
}

/**
 * Turns a server-sent filter state into the client's shape by dropping the
 * `null` optional fields from each filter.
 *
 * Client code tells "has a range" from "has none" with `!== undefined`, and
 * `null !== undefined`: left in, a plain brand filter reads as a window filter
 * with empty bounds (an alert badge printed `Brand: -`). Only `null` is
 * dropped — a real `0` (e.g. `valueType: 0`, "Reviews & Ratings") is kept.
 */
export function dropNullFilterFields<
  S extends { filters: readonly ServerSelectedFilterOption[] },
>(state: S): Omit<S, 'filters'> & { filters: SelectedFilterOption[] } {
  return {
    ...state,
    filters: state.filters.map(
      (filter) =>
        Object.fromEntries(
          Object.entries(filter).filter(([, value]) => value !== null),
        ) as SelectedFilterOption,
    ),
  }
}
